// React
import React, {
  FC,
  useContext,
  useState,
  useEffect,
  useMemo,
  memo,
} from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';
import { getFilterState } from 'Containers/Filter/utilities/filter.utilities';
import { areEqual } from 'Utils/areEqual';
import withErrorBoundary from 'HOC/ErrorBoundary';
import findListsByIdsGql from './gqlQuery';

// Actions
import { setNewListValue } from './actions/attributes.actions';

// Pills
import {
  StandardPill,
  GeographyPill,
  EngagementPill,
  ListMembershipPill,
  SimplePill,
  OverflowPill,
} from './pills';

// Utilities
import { getAttributesArray } from './utilities';

interface PillsProps {
  filterAttributes: string;
}

const Attributes: FC<PillsProps> = (props: any) => {
  const { newList } = useSelector((selectors: any) => selectors.attributes);
  const dispatch = useDispatch();

  const { filterAttributes } = props;

  const [filter, setFilter] = useState(getFilterState(filterAttributes));
  const [attributes, setAttributes] = useState([]);

  const userDetails = useContext(UserDetailsContext);
  const listIds = filter.listMembership.lists.map((list) => list.value);
  const { data: updatedListsData, refetch: refetchSelectedLists } = useQuery(
    findListsByIdsGql,
    {
      variables: {
        instId: userDetails.instId,
        accountId: userDetails.accountId,
        size: 1000,
        listIds,
      },
      fetchPolicy: 'cache-and-network',
      skip: !(listIds.length > 0),
    }
  );

  useEffect(() => {
    setFilter(getFilterState(filterAttributes));
  }, [filterAttributes]);

  useEffect(() => {
    if (listIds.length > 0) {
      refetchSelectedLists();
    }
    setNewListValue(dispatch, false);
  }, [newList]);

  useEffect(() => {
    if (updatedListsData)
      setFilter({
        ...filter,
        listMembership: {
          lists: updatedListsData.paginatedList.list,
        },
      });
  }, [updatedListsData]);

  useEffect(() => {
    setAttributes(getAttributesArray(filter));
  }, [filter, updatedListsData]);

  /**
   * @function mapAttributePills
   *
   * Call this function to map the array of AttributeObjects returned from isValidJSON.
   * This function will take into account the overflowThreshold of the column, which is an
   * arbitrary number right now because checking overflow is not currently possible with this method.
   * a number between 20 to 24 seems to be the spot for the current width.  Once the overflow Threshold 
   * is reached, instead of mapping solely the respective pill types (StandardPill, GeographyPill, EngagementPill, 
   * and SimplePill)  It will construct one of these four pills and then push it into the OverflowPill, to prevent 
   * column overflow.
   *
   * @param {AttributeObject[]} attributes - an AttributeObject that was stringified
   * and sent to the back-end for storage. It is now back on the front end
   * and ready to be parsed into a Pill Attribute.
   * @param {overflowThreshold} number - a number

  * @return {JSX.Element[]} - An array of Attribute Pills that can be dropped
  * straight into JSX with no further mapping.
  *
  * @example
  *
  *     mapAttributePills(attributes, 20)
  */
  const mapAttributePills = (
    attributeList: any[],
    overflowThreshold: number
  ) => {
    let remainingLength = overflowThreshold;
    const overflow: any[] = [];
    const attributePills: any[] = [];

    attributeList.forEach((attribute) => {
      if (attribute.attributes) {
        const splitted = attribute.label.split('=');
        const text = splitted[0];
        const groupCount = `${splitted[1]}`;

        switch (attribute.type) {
          case 'standard': {
            const newStandardPill = (
              <StandardPill
                attribute={attribute}
                text={text}
                key={text}
                groupCount={groupCount}
                focusOnHover={remainingLength - text.length > 0}
              />
            );

            if (remainingLength - text.length > 0) {
              attributePills.push(newStandardPill);
              remainingLength -= text.length;
            } else {
              overflow.push(newStandardPill);
            }
            break;
          }
          case 'geography': {
            const newGeographyPill = (
              <GeographyPill
                attribute={attribute}
                text={text}
                key={text}
                groupCount={groupCount}
                focusOnHover={remainingLength - text.length > 0}
              />
            );

            if (remainingLength - text.length > 0) {
              attributePills.push(newGeographyPill);
              remainingLength -= text.length;
            } else {
              overflow.push(newGeographyPill);
            }
            break;
          }
          case 'engagement': {
            const newEngagementPill = (
              <EngagementPill
                attribute={attribute}
                text={text}
                groupCount={groupCount}
                focusOnHover={remainingLength - text.length > 0}
              />
            );

            if (remainingLength - text.length > 0) {
              attributePills.push(newEngagementPill);
              remainingLength -= text.length;
            } else {
              overflow.push(newEngagementPill);
            }
            break;
          }
          case 'listMembership': {
            const newListMembershipPill = (
              <ListMembershipPill
                attribute={attribute}
                text={text}
                key={text}
                groupCount={groupCount}
                focusOnHover={remainingLength - text.length > 0}
              />
            );

            if (remainingLength - text.length > 0) {
              attributePills.push(newListMembershipPill);
              remainingLength -= text.length;
            } else {
              overflow.push(newListMembershipPill);
            }
            break;
          }
          default:
            break;
        }
      } else {
        const text = attribute.label;
        const newSimplePill = <SimplePill text={text} key={text} />;
        if (remainingLength - text.length > 0) {
          attributePills.push(newSimplePill);
          remainingLength -= text.length;
        } else {
          overflow.push(newSimplePill);
        }
      }
    });

    if (overflow.length > 0) {
      const newOverflowPill = (
        <OverflowPill overflow={overflow} key={overflow.join('')} />
      );

      attributePills.push(newOverflowPill);
    }

    return attributePills;
  };

  const memoizeMappedAttributePills = () =>
    useMemo(() => {
      return mapAttributePills(
        attributes,
        (attributes.length === 1 && 32) || 22
      );
    }, [attributes, updatedListsData]);

  const memoizedMappedAttributePills = memoizeMappedAttributePills();

  return <>{memoizedMappedAttributePills}</>;
};

export default withErrorBoundary(memo(Attributes, areEqual));
